import Link from 'next/link'
import { cn } from '@/lib/utils'

export type RetargetingPlatform = 'meta' | 'google' | 'tiktok' | 'reddit' | 'linkedin' | 'x'
export type RetargetingSyncStatus = 'pending' | 'active' | 'failed' | 'removed'
export type RetargetingAudienceStatus = 'draft' | 'building' | 'active' | 'paused' | 'archived'
export type RetargetingAudienceMode = 'mirror' | 'orchestrate' | 'full_stack'

export type AudienceListItem = {
  id: string
  name: string
  description: string | null
  mode: RetargetingAudienceMode
  status: RetargetingAudienceStatus
  member_count: number
  created_at: string
  syncs: Array<{
    platform: RetargetingPlatform
    platform_audience_id: string | null
    status: RetargetingSyncStatus
    match_rate: number | null
    platform_size: number | null
    last_synced_at: string | null
    last_error: string | null
  }>
}

export function AudienceListRow({ audience }: { audience: AudienceListItem }) {
  const failedSync = audience.syncs.some(sync => sync.status === 'failed')

  return (
    <Link
      href={`/customers/audiences/${audience.id}`}
      className="group flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-warm focus:outline-none focus-visible:bg-warm focus-visible:outline-2 focus-visible:outline-brand/40"
    >
      <span
        className={cn(
          'size-2.5 shrink-0 rounded-full',
          failedSync
            ? 'bg-red-500'
            : audience.status === 'active'
              ? 'bg-emerald-500'
              : audience.status === 'building' || audience.status === 'draft'
                ? 'bg-amber-500'
                : 'bg-meta/45',
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-text-strong">{audience.name}</div>
        <div className="mt-1 truncate text-[13px] text-text-muted">
          {audience.description ? `${audience.description} · ` : ''}
          {formatNumber(audience.member_count)} members
        </div>
      </div>
      <div className="hidden min-w-[180px] flex-col items-end gap-1 sm:flex">
        <div className="flex max-w-full flex-wrap justify-end gap-1.5">
          {audience.syncs.length > 0 ? (
            audience.syncs.map(sync => (
              <span
                key={sync.platform}
                className="rounded-full border border-line bg-surface px-2 py-0.5 text-[11px] font-medium text-text-strong"
              >
                {platformName(sync.platform)}
              </span>
            ))
          ) : (
            <span className="text-[12px] text-text-muted">No syncs</span>
          )}
        </div>
        {audience.syncs.length > 0 && (
          <div className="truncate text-[12px] tabular-nums text-text-muted">
            {audience.syncs.map(sync => formatMatchRate(sync.match_rate)).join(' / ')}
          </div>
        )}
      </div>
    </Link>
  )
}

function formatNumber(value: number) {
  return new Intl.NumberFormat('en-US').format(value)
}

export function platformName(platform: RetargetingPlatform) {
  const names: Record<RetargetingPlatform, string> = {
    meta: 'Meta',
    google: 'Google',
    tiktok: 'TikTok',
    reddit: 'Reddit',
    linkedin: 'LinkedIn',
    x: 'X',
  }
  return names[platform]
}

function formatMatchRate(value: number | null) {
  if (value == null) return '-'
  return `${Math.round(value * 100)}%`
}
